import { actionsThatChangeStatus } from '../../contexts/order.utils';
import { OrderStatus } from '../../interfaces/order-status.interface';
import { StatusType, SynchronizedType } from '../../order.types';
import { OrderGatewayPaymentStatus } from '../../payment/enums/orderPaymentGatewayStatus.enum';
import { OrderPaymentActionsUtils } from '../../payment/orderPaymentActions.utils';
import { GetActionsToRemoveBasedOnGatewayPaymentParams, Role } from '../types/actions.types';
import { ActionEnum } from '../types/orderActions.types';

export const allRoles = [
  Role.ADMIN,
  Role.ADMIN_LOJA,
  Role.ADMIN_MARCA,
  Role.COMPRADOR,
  Role.VENDEDOR,
];

export const brandRoles = [Role.ADMIN_MARCA, Role.VENDEDOR];

type RemoveActionsIntegration = {
  hasIntegrations: boolean;
  synchronized: SynchronizedType;
  triggerIntegrationConfig?: OrderStatus;
  status: OrderStatus;
};

const getActionsToRemoveOnSuccessIntegration = (status: OrderStatus): ActionEnum[] => {
  switch (status) {
    case StatusType.SENT:
      return [ActionEnum.BACK_TO_APPROVED];

    case StatusType.APPROVED:
      return [ActionEnum.BACK_TO_ON_APPROVAL, ActionEnum.MOVE_TO_SENT];

    case StatusType.ON_APPROVAL:
      return [ActionEnum.BACK_TO_IN_REVISION, ActionEnum.FINISH_TO_APPROVED];

    case StatusType.CANCELED:
      return [ActionEnum.CONFIRM_BACK_TO_DRAFT];

    default:
      return [];
  }
};

const getActionsToRemoveOnErrorIntegrationAndMoveToSentStatus = (
  triggerIntegrationConfig?: OrderStatus,
): ActionEnum[] => {
  if (triggerIntegrationConfig === StatusType.SENT) {
    return [];
  }
  return [ActionEnum.MOVE_TO_SENT];
};

const getActionsToRemoveOnErrorIntegrationAndOnApprovalStatus = (
  triggerIntegrationConfig?: OrderStatus,
): ActionEnum[] => {
  if (triggerIntegrationConfig === StatusType.ON_APPROVAL) {
    return [ActionEnum.FINISH_TO_APPROVED];
  }
  return [];
};

const getActionsToRemoveOnErrorIntegration = (
  status: OrderStatus,
  triggerIntegrationConfig?: OrderStatus,
): ActionEnum[] => {
  switch (status) {
    case StatusType.APPROVED:
      return getActionsToRemoveOnErrorIntegrationAndMoveToSentStatus(triggerIntegrationConfig);
    case StatusType.ON_APPROVAL:
      return getActionsToRemoveOnErrorIntegrationAndOnApprovalStatus(triggerIntegrationConfig);
    default:
      return [];
  }
};

const getActionsToRemoveWhenRejected = (status: OrderStatus, synchronized: SynchronizedType) => {
  if (synchronized !== SynchronizedType.NOT_SYNCHRONIZED && status === StatusType.REJECTED) {
    return [...actionsThatChangeStatus];
  }

  return [];
};

export const getActionsToRemoveBasedOnIntegrationStatus = ({
  hasIntegrations,
  synchronized,
  triggerIntegrationConfig,
  status,
}: RemoveActionsIntegration): ActionEnum[] => {
  if (!hasIntegrations) {
    return [];
  }

  switch (synchronized) {
    case SynchronizedType.SUCCESS:
      return [
        ActionEnum.SYNC,
        ActionEnum.MOVE_TO_CANCELED,
        ActionEnum.MOVE_TO_REJECTED,
        ActionEnum.OPEN_UPDATE_ORDER_MODAL,
        ...getActionsToRemoveOnSuccessIntegration(status),
        ...getActionsToRemoveWhenRejected(status, synchronized),
      ];
    case SynchronizedType.ERROR:
      return [
        ActionEnum.MOVE_TO_REJECTED,
        ...getActionsToRemoveWhenRejected(status, synchronized),
        ...getActionsToRemoveOnErrorIntegration(status, triggerIntegrationConfig),
      ];
    case SynchronizedType.SUCCESS_WITH_PAYMENT_ERROR:
      return [
        ActionEnum.MOVE_TO_CANCELED,
        ActionEnum.MOVE_TO_REJECTED,
        ActionEnum.OPEN_UPDATE_ORDER_MODAL,
        ...getActionsToRemoveOnSuccessIntegration(status).filter(
          (action) => action !== ActionEnum.MOVE_TO_SENT,
        ),
        ...getActionsToRemoveWhenRejected(status, synchronized),
      ];
    case SynchronizedType.PROCESSING:
      return [
        ActionEnum.MOVE_TO_REJECTED,
        ActionEnum.BACK_TO_IN_REVISION,
        ActionEnum.BACK_TO_ON_APPROVAL,
        ActionEnum.BACK_TO_APPROVED,
        ActionEnum.FINISH_TO_APPROVED,
        ActionEnum.MOVE_TO_SENT,
        ActionEnum.MOVE_TO_CANCELED,
        ActionEnum.OPEN_UPDATE_ORDER_MODAL,
        ...getActionsToRemoveWhenRejected(status, synchronized),
      ];
    case SynchronizedType.PROCESSING_AFTER_RESET:
    case SynchronizedType.SUCCESS_RESET:
      return [ActionEnum.MOVE_TO_REJECTED, ...getActionsToRemoveWhenRejected(status, synchronized)];
    default:
      return [];
  }
};

export type FeatureFlagToVerify = {
  valueSystemParam: boolean | undefined;
  actionEnum: ActionEnum;
};

export const getActionsToRemoveBySystemParams = (features: FeatureFlagToVerify[]): ActionEnum[] =>
  features.filter((feature) => !feature.valueSystemParam).map((feature) => feature.actionEnum);

export const getActionToRemoveBasedOnArchivedOrder = (archivedOrder: boolean): ActionEnum[] =>
  archivedOrder ? [ActionEnum.ARCHIVE_ORDER, ActionEnum.SHARE_ORDER] : [ActionEnum.UNARCHIVE_ORDER];

export const getActionsToRemoveBasedOnGatewayPayment = ({
  ability,
  params,
  role,
  integrationTrigger,
}: GetActionsToRemoveBasedOnGatewayPaymentParams): ActionEnum[] => {
  const actionsToRemove: ActionEnum[] = [];

  const orderPaymentActionUtils = new OrderPaymentActionsUtils({
    order: {
      paymentStatus: params.orderPaymentStatus,
      splitted: params.splitted,
      status: params.status as OrderStatus,
      type: params.orderType,
      synchronized: params.synchronized,
    },
    lastGatewayPaymentStatus: params.lastGatewayPaymentStatus,
    lastGatewayPaymentUpdatedAt: params.lastGatewayPaymentUpdatedAt,
    refundLimitHours: params.refundLimitHours,
    ability,
    requirePaymentGateway: params.requirePaymentGateway,
    requirePaymentReleaseForImmediate: params.requirePaymentReleaseForImmediate,
    requirePaymentReleaseForPreOrder: params.requirePaymentReleaseForPreOrder,
    role,
    integrationTrigger,
  });

  if (!orderPaymentActionUtils.showReleasePaymentButton()) {
    actionsToRemove.push(ActionEnum.AUTHORIZE_ORDER_PAYMENT);
  }

  if (
    !orderPaymentActionUtils.showMakePaymentButton() ||
    !orderPaymentActionUtils.userCanMakePayment()
  ) {
    actionsToRemove.push(ActionEnum.OPEN_ORDER_PAYMENT_MODAL);
  }

  if (!orderPaymentActionUtils.showHistoryPaymentButton()) {
    actionsToRemove.push(ActionEnum.OPEN_ORDER_PAYMENT_HISTORY_MODAL);
  }

  if (!orderPaymentActionUtils.showCancelPaymentButton()) {
    actionsToRemove.push(ActionEnum.OPEN_CANCEL_PAYMENT_LINK_MODAL);
  }

  if (!orderPaymentActionUtils.showRefundPaymentButton()) {
    actionsToRemove.push(ActionEnum.OPEN_ORDER_REFUND_PAYMENT_MODAL);
  }

  const availableStatus = [
    OrderGatewayPaymentStatus.CANCELED,
    OrderGatewayPaymentStatus.REFUNDED,
    OrderGatewayPaymentStatus.EXPIRED,
  ];

  if (
    params.lastGatewayPaymentStatus &&
    !availableStatus.includes(params.lastGatewayPaymentStatus)
  ) {
    actionsToRemove.push(
      ActionEnum.BACK_TO_ON_APPROVAL,
      ActionEnum.BACK_TO_APPROVED,
      ActionEnum.BACK_TO_DRAFT,
      ActionEnum.BACK_TO_IN_REVISION,
      ActionEnum.CONFIRM_BACK_TO_DRAFT,
      ActionEnum.MOVE_TO_CANCELED,
      ActionEnum.MOVE_TO_REJECTED,
      ActionEnum.OPEN_UPDATE_ORDER_MODAL,
    );
  }

  return actionsToRemove;
};
